import React from 'react';
import CustomDropdown from './CustomDropdown'; // Import the custom dropdown
import './ProductFilter.css';
import type { ProductOption } from '../types/productTypes';
import { trackComparisonPhoneSelected, trackComparisonShopNow } from '../helpers/analytics';

interface ProductFilterProps {
  availableProducts: ProductOption[];
  initialSelectedProducts?: string[];
  maxProducts?: number;
  onSelectionChange: (selectedProductIds: string[]) => void;
  onToggleAdvancedFilters?: () => void;
  showAdvancedFilters?: boolean;
  onResetFilters?: () => void;
  children?: React.ReactNode;
}

const ProductFilter: React.FC<ProductFilterProps> = ({
  availableProducts,
  initialSelectedProducts = [],
  maxProducts = 3,
  onSelectionChange,
  onToggleAdvancedFilters,
  showAdvancedFilters = false,
  onResetFilters,
  children,
}) => {
  /**
   * AIDEV-NOTE: Controlled component pattern for dropdown selections
   * We're using a controlled component pattern where the parent (CompareApp) maintains
   * the source of truth for selected products. The ProductFilter displays those selections
   * and reports changes back to the parent.
   */
  const selectedProducts = initialSelectedProducts.slice(0, maxProducts);

  const handleLocalProductChange = (newProductId: string, index: number) => {
    const updatedSelection = [...selectedProducts];
    // Ensure the array is long enough to hold the selection at the specified index
    while (updatedSelection.length <= index) {
      updatedSelection.push('');
    }
    updatedSelection[index] = newProductId;
    onSelectionChange(updatedSelection);
  };

  const handleProductChange = (newProductId: string, index: number) => {
    handleLocalProductChange(newProductId, index);

    if (newProductId) {
      const selectedProduct = availableProducts.find(product => product.id === newProductId);
      if (selectedProduct) {
        trackComparisonPhoneSelected(selectedProduct, index);
      }
    }
  };

  const renderDropdowns = () => {
    const dropdowns = [];

    for (let i = 0; i < maxProducts; i++) {
      // Group products by productType (Type field)
      const typeGroups: { [type: string]: ProductOption[] } = {};

      // Define default type for products without a productType
      const DEFAULT_TYPE = "Other";

      // Custom category order
      const CATEGORY_ORDER = ["iPhone", "Galaxy S", "Pixel", "iPad"];

      // Organize products into type groups
      availableProducts.forEach(product => {
        // Extract base product type to group similar products together
        let productType = product.productType || DEFAULT_TYPE;

        // Normalize product types for more consistent categorization
        const productTypeLower = productType.toLowerCase();

        if (productTypeLower.includes('iphone')) {
          productType = "iPhone";
        } else if (productTypeLower.includes('galaxy s') || productTypeLower.match(/galaxy\s+s\d/i)) {
          productType = "Galaxy S";
        } else if (productTypeLower.includes('pixel')) {
          productType = "Pixel";
        } else if (productTypeLower.includes('ipad')) {
          productType = "iPad";
        }

        // Create the array if it doesn't exist yet
        if (!typeGroups[productType]) {
          typeGroups[productType] = [];
        }

        // TypeScript safety: add the product to the array that we just created
        typeGroups[productType]?.push(product);
      });

      // Custom sort for categories based on CATEGORY_ORDER
      const sortedTypes = Object.keys(typeGroups).sort((a, b) => {
        const indexA = CATEGORY_ORDER.indexOf(a);
        const indexB = CATEGORY_ORDER.indexOf(b);

        // If both categories are in the predefined order
        if (indexA >= 0 && indexB >= 0) {
          return indexA - indexB;
        }

        // If only one category is in the predefined order
        if (indexA >= 0) return -1;
        if (indexB >= 0) return 1;

        // For categories not in the predefined order, sort alphabetically
        return a.localeCompare(b);
      });

      // Sort products within each category by release date (if available)
      Object.keys(typeGroups).forEach(type => {
        if (typeGroups[type]) {
          typeGroups[type].sort((a, b) => {
            // Helper function to extract date from release date metafield
            const extractDate = (product: ProductOption): Date => {
              // First check if we have a releaseDate from the metafield
              if (product.releaseDate) {
                // Try to parse the release date string directly - JavaScript Date constructor
                // can handle formats like "September 21, 2018", "November 3, 2017", etc.
                const parsedDate = new Date(product.releaseDate);

                // Check if the parsed date is valid
                if (!isNaN(parsedDate.getTime())) {
                  return parsedDate;
                }
              }

              // If no release date is available, return Unix epoch start
              return new Date(0); // January 1, 1970
            };

            const dateA = extractDate(a);
            const dateB = extractDate(b);

            // First sort by date in descending order (newer first)
            const dateDiff = dateB.getTime() - dateA.getTime();
            if (dateDiff !== 0) {
              return dateDiff;
            }

            // If dates are the same, sort by model hierarchy for same release date
            if (type === "iPhone") {
              const getIphoneModelPriority = (productName: string): number => {
                const nameLower = productName.toLowerCase();
                if (nameLower.includes('pro max')) return 1;
                if (nameLower.includes('pro')) return 2;
                if (nameLower.includes('plus')) return 3;
                if (nameLower.includes('xs max')) return 3.5; // XS Max should come before regular XS
                if (nameLower.includes('mini')) return 5;
                return 4; // Regular model
              };

              const priorityA = getIphoneModelPriority(a.name);
              const priorityB = getIphoneModelPriority(b.name);

              if (priorityA !== priorityB) {
                return priorityA - priorityB;
              }
            }

            // Galaxy S phone hierarchy: Ultra > 5G Plus > Plus > 5G > Base > FE
            if (type === "Galaxy S") {
              const getGalaxyModelPriority = (productName: string): number => {
                const nameLower = productName.toLowerCase();
                if (nameLower.includes('ultra')) return 1;
                // Check more specific patterns first
                if (nameLower.includes('5g plus') || nameLower.includes('5g+')) return 2;
                if (nameLower.includes('plus') || nameLower.includes('+')) return 3;
                if (nameLower.includes('5g')) return 4;
                if (nameLower.includes('fe')) return 6;
                return 5; // Base model
              };

              const priorityA = getGalaxyModelPriority(a.name);
              const priorityB = getGalaxyModelPriority(b.name);

              if (priorityA !== priorityB) {
                return priorityA - priorityB;
              }
            }

            // Pixel phone hierarchy: Pro XL > Pro > Base > a variant
            if (type === "Pixel") {
              const getPixelModelPriority = (productName: string): number => {
                const nameLower = productName.toLowerCase();
                if (nameLower.includes('pro xl')) return 1;
                if (nameLower.includes('pro')) return 2;
                // Check for "a" variant more specifically - look for patterns like "pixel 8a", "pixel 7a"
                if (nameLower.match(/pixel\s*\d+a\b/) || nameLower.includes(' a ') || nameLower.endsWith(' a')) return 4;
                return 3; // Base model
              };

              const priorityA = getPixelModelPriority(a.name);
              const priorityB = getPixelModelPriority(b.name);

              if (priorityA !== priorityB) {
                return priorityA - priorityB;
              }
            }

            // If same date and same model type, sort alphabetically
            return a.name.localeCompare(b.name);
          });
        }
      });

      // Create dropdown options with category headers
      type DropdownOptionType = {
        id: string;
        name: string;
        isCategoryHeader?: boolean;
        brand?: string;
        productType?: string;
        url?: string;
        disabled?: boolean;
      };
      const dropdownOptions: DropdownOptionType[] = [];

      sortedTypes.forEach(productType => {
        // Add type category header
        dropdownOptions.push({
          id: `header-${productType}`,
          name: productType,
          isCategoryHeader: true
        });

        // Add products under this type
        if (typeGroups[productType]) {
          typeGroups[productType].forEach(product => {
            const option: DropdownOptionType = {
              id: product.id,
              name: product.name,
              disabled: selectedProducts.includes(product.id) && selectedProducts[i] !== product.id
            };

            // Add optional properties if they exist
            if (product.brand) option.brand = product.brand;
            if (product.productType) option.productType = product.productType;
            if (product.url) option.url = product.url;

            dropdownOptions.push(option);
          });
        }
      });

      dropdowns.push(
        <div key={i} className="phone-selector-group">
          {i > 0 && (
            <label htmlFor={`phone${i + 1}-select`} className="visually-hidden">
              Compare
            </label>
          )}
          <CustomDropdown
            id={`phone${i + 1}-select`}
            name={`phone${i + 1}`}
            value={selectedProducts[i] || ''}
            options={dropdownOptions}
            placeholder="Select a phone"
            onChange={(value) => handleProductChange(value, i)}
          />
          <a
            href={selectedProducts[i] ? availableProducts.find(product => product.id === selectedProducts[i])?.url || '#' : '#'}
            className={`shop-now-small ${!selectedProducts[i] ? 'disabled' : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (!selectedProducts[i]) {
                e.preventDefault();
              } else {
                const selectedProduct = availableProducts.find(product => product.id === selectedProducts[i]);
                if (selectedProduct) {
                  trackComparisonShopNow(selectedProduct, i);
                }
              }
            }}
          >
            Shop Now
          </a>
        </div>
      );

      // Note: vs separators are now handled by CSS pseudo-elements
    }

    return dropdowns;
  };

  return (
    <div className="filter-controls-wrapper">
      <div className="filter-controls-header">
        <div className="compare-label">Compare</div>
        <div className="filter-controls-actions">
          {onResetFilters && (
            <a
              href="#"
              className="clear-filters-link"
              onClick={(e) => {
                e.preventDefault();
                onResetFilters();
              }}
            >
              Clear filters
            </a>
          )}
          <a
            href="#"
            className="expand-filters-link"
            onClick={(e) => {
              e.preventDefault();
              if (onToggleAdvancedFilters) onToggleAdvancedFilters();
            }}
          >
            {showAdvancedFilters ? 'Hide filters' : 'Expand filters'} <span className="plus-icon">{showAdvancedFilters ? '-' : '+'}</span>
          </a>
        </div>
      </div>
      {/* Render children (AdvancedFilters) directly after the header */}
      {children}
      <div className="filter-controls-row">{renderDropdowns()}</div>
    </div>
  );
};

export default ProductFilter;
